/*
 Note:
 This component is included directly into the sources of this project to simplify the
 build process.

 License:
 Copyright 2019, Indoqa Software Design und Beratung GmbH

 Permission is hereby granted, free of charge, to any person obtaining a copy
 of this software and associated documentation files (the "Software"), to deal
 in the Software without restriction, including without limitation the rights
 to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 copies of the Software, and to permit persons to whom the Software is
 furnished to do so, subject to the following conditions:
 The above copyright notice and this permission notice shall be included in all
 copies or substantial portions of the Software.

 THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL
 THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import {Flex} from '@indoqa/style-system'
import {IStyle} from 'fela'
import * as React from 'react'
import {treeNodeContextMenu, treeNodeOnMouseOut, treeNodeOnMouseOver} from './treeNodeEvents'
import {TreeViewContextValue} from './treeView.types'
import {calcIndent, createEventHandlers, cursorPointerStyle, overflowHiddenStyle} from './treeView.utils'
import {TreeViewContext} from './TreeViewContext'

interface Props<S> {
  structure: S
  level: number
  first: boolean
  last: boolean
}

export function TreeLeaf<S>(props: Props<S>) {
  const {structure, level, first, last} = props
  const {structureAccessors, selectedNodePath, style, renderLeafNode, scrollTo} =
    React.useContext<TreeViewContextValue<S>>(TreeViewContext)
  const [hover, setHover] = React.useState(false)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const treeViewContext = React.useContext(TreeViewContext)
  const {selectedNode} = treeViewContext
  const nodeId = structureAccessors.getId(structure)
  const activeLeaf = structureAccessors.isActive(structure)

  React.useEffect(() => {
    if (containerRef.current && selectedNode.id === nodeId && !treeViewContext.scrolledToSelectedNode) {
      scrollTo(containerRef.current.offsetTop, containerRef.current.offsetHeight)
      treeViewContext.scrolledToSelectedNode = true
    }
  }, [selectedNode.id, nodeId, scrollTo, treeViewContext, treeViewContext.scrolledToSelectedNode])

  const selectedStyle: IStyle = {
    backgroundColor: selectedNode.id === nodeId ? style.colorSelected : hover ? style.colorHover : 'transparent',
  }
  const paddingLeftIndentationStyle: IStyle = {
    paddingLeft: calcIndent(level + 1, level + 1, style),
    flexBasis: calcIndent(level + 1, level + 1, style),
    flexShrink: 0,
  }
  const styles = [overflowHiddenStyle, selectedStyle, paddingLeftIndentationStyle]
  if (activeLeaf) {
    styles.push(cursorPointerStyle)
  }
  const eventHandlers = createEventHandlers()
  const inNodePath = selectedNodePath.includes(nodeId)
  const leafPanel = renderLeafNode({
    structure,
    level,
    first,
    last,
    eventHandlers,
    style,
    selected: inNodePath,
    inNodePath,
  })
  const treeLeafOnClick = (e: React.MouseEvent<HTMLElement>) => {
    if (!activeLeaf) {
      return
    }
    e.stopPropagation()
    eventHandlers.click.emit({timeStamp: e.timeStamp})
  }
  return (
    <Flex
      nowrap
      style={styles}
      innerRef={containerRef}
      htmlAttrs={{
        onClick: treeLeafOnClick,
        onMouseOver: treeNodeOnMouseOver(eventHandlers),
        onMouseOut: treeNodeOnMouseOut(eventHandlers, containerRef, setHover),
        onContextMenu: treeNodeContextMenu(eventHandlers),
      }}
    >
      {leafPanel}
    </Flex>
  )
}
